import { DATE_FORMAT, DELAY_STATUS, FULL_DATETIME_FORMAT, MODE_AIR, MODE_TRANSFER } from '@/lib/constants';
import { FlightServiceModel } from '@/lib/models/flight-service/types';
import { ItineraryLegModel, ItineraryModel } from '@/lib/models/itinerary/types';
import { LineDictionaryModel } from '@/lib/models/lines/types';
import { getLineCodeshares } from '@/lib/models/lines/utils/get-line-codeshares';
import { SpotRateModel } from '@/lib/models/rates/types';
import { RouteScheduleInterface, RouteScheduleSegmentInterface } from '@/types';
import { formatToLocationDateTime } from '@/utils/format';
import { addDelayToDate } from '@/utils/format/date-time';
import { mergeScheduleSegmentGroups } from '@/utils/schedules';

const formatTime = (time: string) => time.slice(0, 5);

/**
 * Flight Service -> Itinerary
 */
export const transformFlightServiceToItineraryLeg = (
  flightService: FlightServiceModel,
  fromId: string,
  toId: string,
): ItineraryLegModel => {
  const {
    id,
    carrierId,
    vehicleId,
    toTerminal,
    fromTerminal,
    durationSeconds,
    departureTime,
    arrivalTime,
    emissions,
    effectiveRange,
    operatingDays,
    flightNumber,
  } = flightService;

  return {
    id,
    mode: MODE_AIR,
    fromId,
    toId,
    stopIds: [], // @TODO get stop data shape changed in Flight Services API
    fromTerminal,
    toTerminal,
    operatingCarrierId: carrierId,
    vehicleId,
    durationSeconds,
    emissions,
    flightNumber,
    std: formatTime(departureTime),
    sta: formatTime(arrivalTime),
    isPredicted: false,
    operatingCalendar: {
      effectiveDates: effectiveRange,
      operatingDays,
    },
  };
};

/**
 * Route Schedule -> Itinerary
 */
export const transformRouteScheduleToItinerary = (
  schedule: RouteScheduleInterface,
  lineDictionary: LineDictionaryModel,
  rates?: SpotRateModel[],
): ItineraryModel => {
  const sequences = mergeScheduleSegmentGroups(schedule.segments);
  const withOutTransfers = sequences.filter((segment) => segment.mode !== MODE_TRANSFER);
  const { primaryCarriers } = schedule.attributes;

  return {
    fromId: schedule.from.nodeReference,
    toId: schedule.to.nodeReference,
    type: schedule.mode,
    durationSeconds: schedule.duration,
    legs: withOutTransfers.map((segment) =>
      transformRouteScheduleSegmentToItineraryLeg(segment, lineDictionary, primaryCarriers),
    ),
    carrierIds: primaryCarriers,
    rates,
  };
};

/**
 * Route Schedule Segment -> Itinerary Leg
 */
export const transformRouteScheduleSegmentToItineraryLeg = (
  segment: RouteScheduleSegmentInterface,
  lineDictionary?: LineDictionaryModel,
  primaryCarriers?: string[],
): ItineraryLegModel => {
  const {
    id,
    from,
    to,
    mode,
    duration,
    fromTerminal,
    toTerminal,
    carrierId: operatingCarrierId,
    stopIds,
    lineIds,
    vehicleId,
    isPredicted,
    attributes,
    departureDateTime,
    arrivalDateTime,
    emissions,
  } = segment;

  // Delays
  const { vehicleDelay, flightNumber } = attributes;
  const isDelayed = vehicleDelay?.status === DELAY_STATUS.DELAYED;

  // Get Timezones
  const departureTimezone = from.timezoneName;
  const arrivalTimezone = to?.timezoneName;

  // Format Scheduled Datetime to local date and time
  const std =
    departureDateTime && departureTimezone
      ? formatToLocationDateTime(departureDateTime, departureTimezone, FULL_DATETIME_FORMAT)
      : '';
  const sta =
    arrivalDateTime && arrivalTimezone
      ? formatToLocationDateTime(arrivalDateTime, arrivalTimezone, FULL_DATETIME_FORMAT)
      : '';

  // Format Estimated Datetime to local date and time
  const predictedDepartureDateTime =
    isDelayed && departureDateTime ? addDelayToDate(departureDateTime, vehicleDelay.days) : null;
  const predictedArrivalDateTime =
    isDelayed && arrivalDateTime ? addDelayToDate(arrivalDateTime, vehicleDelay.days) : null;

  const etd =
    predictedDepartureDateTime && departureTimezone
      ? formatToLocationDateTime(predictedDepartureDateTime, departureTimezone, DATE_FORMAT)
      : '';
  const eta =
    predictedArrivalDateTime && arrivalTimezone
      ? formatToLocationDateTime(predictedArrivalDateTime, arrivalTimezone, DATE_FORMAT)
      : '';

  // Get Line info and line Codeshares (if ocean vessel)
  const line = lineIds?.length && lineDictionary ? lineDictionary[lineIds[0]] : undefined;
  const codeshares = getLineCodeshares(line, operatingCarrierId, primaryCarriers);

  return {
    id,
    mode,
    fromId: from.nodeReference,
    toId: to?.nodeReference || '',
    fromTerminal,
    toTerminal,
    operatingCarrierId,
    stopIds: stopIds || [],
    vehicleId,
    status: vehicleDelay?.status || 'ontime',
    durationSeconds: duration,
    flightNumber,
    isPredicted: isPredicted || false,
    emissions,
    codeshares,
    std,
    sta,
    etd,
    eta,
  };
};
